/**
 * Global, per-browser persistence for the right-side "Space Chat" panel size.
 *
 * The panel size is intentionally NOT per-space and NOT synced to the backend —
 * it is a personal viewport preference. We persist it in localStorage so it
 * survives page reloads, space switches, and creating new spaces. This stops
 * the panel from being silently re-snapped to a default in any of these
 * situations.
 *
 * Four independent values are tracked:
 *   - Desktop focused/standalone width (px)        → `riverChatPanelWidth`
 *   - Desktop focused height (px, nullable)        → `riverChatPanelFocusedHeightPx`
 *   - Desktop spatial/floating width (px)          → `riverChatPanelWidthSpatial`
 *   - Desktop spatial/floating height (px, null)   → `riverChatPanelHeightSpatial`
 *   - Mobile chat panel height (vh percentage)     → `riverChatPanelHeightVh`
 *
 * Spatial uses its own keys because the spatial layout has a wider canvas than
 * the focused layout, and users typically prefer a different size there.
 */

import { useEffect, useState, useCallback, useRef } from 'react';

const KEY_FOCUSED_WIDTH = 'riverChatPanelWidth';
const KEY_FOCUSED_HEIGHT = 'riverChatPanelFocusedHeightPx';
const KEY_SPATIAL_WIDTH = 'riverChatPanelWidthSpatial';
const KEY_SPATIAL_HEIGHT = 'riverChatPanelHeightSpatial';
const KEY_MOBILE_HEIGHT_VH = 'riverChatPanelHeightVh';

export const CHAT_PANEL_FOCUSED_DEFAULT_PX = 450;
export const CHAT_PANEL_FOCUSED_MIN_PX = 280;
export const CHAT_PANEL_FOCUSED_MAX_PX = 900;

// null means "fill full container height" — the default. Once the user drags
// the top edge, a pixel value is stored and the panel becomes height-constrained.
export const CHAT_PANEL_FOCUSED_HEIGHT_DEFAULT: number | null = null;
export const CHAT_PANEL_FOCUSED_HEIGHT_MIN_PX = 300;
export const CHAT_PANEL_FOCUSED_HEIGHT_MAX_PX = 1400;

export const CHAT_PANEL_SPATIAL_DEFAULT_PX = 500;
export const CHAT_PANEL_SPATIAL_MIN_PX = 350;
export const CHAT_PANEL_SPATIAL_MAX_PX = 800;

// null = use CSS default (min(72vh, …)). Set once the user drags a height edge.
export const CHAT_PANEL_SPATIAL_HEIGHT_MIN_PX = 300;
export const CHAT_PANEL_SPATIAL_HEIGHT_MAX_PX = 1400;

export const CHAT_PANEL_MOBILE_DEFAULT_VH = 33.33;
export const CHAT_PANEL_MOBILE_MIN_VH = 30;
export const CHAT_PANEL_MOBILE_MAX_VH = 85;

// First-signup users get a wider chat panel than the standard default so the
// conversational onboarding feels front-and-center. These are one-time seed
// values, applied only when the user has no stored size yet (see
// seedFirstSignupChatPanelSize) — a returning user who resized the panel is
// never overridden.
export const CHAT_PANEL_SPATIAL_FIRST_SIGNUP_PX = 680;
export const CHAT_PANEL_FOCUSED_FIRST_SIGNUP_PX = 620;

// In-chat default onboarding (onboardingStore.endChatOnboarding) docks the
// panel to the bottom-right once the persona + first-message steps are done.
// A little bigger than the plain default feels more substantial for a user
// who was just front-and-center in the large onboarding intake. Only a modest
// bump above CHAT_PANEL_SPATIAL_DEFAULT_PX — the first-signup seed above may
// already have set a wider value by this point, which this never overrides.
export const CHAT_PANEL_SPATIAL_POST_ONBOARDING_PX = 620;

const clamp = (value: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
};

const safeReadNumber = (key: string): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const safeWriteNumber = (key: string, value: number): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // localStorage may be unavailable (private mode, quota). Silently ignore —
    // the panel will still resize for the session, just without persistence.
  }
};

export const readChatPanelWidth = (): number => {
  const stored = safeReadNumber(KEY_FOCUSED_WIDTH);
  if (stored === null) return CHAT_PANEL_FOCUSED_DEFAULT_PX;
  return clamp(stored, CHAT_PANEL_FOCUSED_MIN_PX, CHAT_PANEL_FOCUSED_MAX_PX);
};

// Writers return the clamped value they wrote. Returning it (instead of
// re-reading from storage) means the in-memory UI state stays in sync with
// the user's drag even when localStorage is read-only (private mode, quota).
export const writeChatPanelWidth = (px: number): number => {
  const clamped = clamp(px, CHAT_PANEL_FOCUSED_MIN_PX, CHAT_PANEL_FOCUSED_MAX_PX);
  safeWriteNumber(KEY_FOCUSED_WIDTH, clamped);
  return clamped;
};

// Returns null when the user has never set a height (panel fills full container height).
export const readChatPanelFocusedHeight = (): number | null => {
  const stored = safeReadNumber(KEY_FOCUSED_HEIGHT);
  if (stored === null) return null;
  return clamp(stored, CHAT_PANEL_FOCUSED_HEIGHT_MIN_PX, CHAT_PANEL_FOCUSED_HEIGHT_MAX_PX);
};

export const writeChatPanelFocusedHeight = (px: number): number => {
  const clamped = clamp(px, CHAT_PANEL_FOCUSED_HEIGHT_MIN_PX, CHAT_PANEL_FOCUSED_HEIGHT_MAX_PX);
  safeWriteNumber(KEY_FOCUSED_HEIGHT, clamped);
  return clamped;
};

export const clearChatPanelFocusedHeight = (): void => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(KEY_FOCUSED_HEIGHT); } catch {}
};

export const readChatPanelSpatialWidth = (): number => {
  const stored = safeReadNumber(KEY_SPATIAL_WIDTH);
  if (stored === null) return CHAT_PANEL_SPATIAL_DEFAULT_PX;
  return clamp(stored, CHAT_PANEL_SPATIAL_MIN_PX, CHAT_PANEL_SPATIAL_MAX_PX);
};

export const writeChatPanelSpatialWidth = (px: number): number => {
  const clamped = clamp(px, CHAT_PANEL_SPATIAL_MIN_PX, CHAT_PANEL_SPATIAL_MAX_PX);
  safeWriteNumber(KEY_SPATIAL_WIDTH, clamped);
  return clamped;
};

// Returns null when the user has never set a height (panel uses CSS default).
export const readChatPanelSpatialHeight = (): number | null => {
  const stored = safeReadNumber(KEY_SPATIAL_HEIGHT);
  if (stored === null) return null;
  return clamp(stored, CHAT_PANEL_SPATIAL_HEIGHT_MIN_PX, CHAT_PANEL_SPATIAL_HEIGHT_MAX_PX);
};

export const writeChatPanelSpatialHeight = (px: number): number => {
  const clamped = clamp(px, CHAT_PANEL_SPATIAL_HEIGHT_MIN_PX, CHAT_PANEL_SPATIAL_HEIGHT_MAX_PX);
  safeWriteNumber(KEY_SPATIAL_HEIGHT, clamped);
  return clamped;
};

export const clearChatPanelSpatialHeight = (): void => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(KEY_SPATIAL_HEIGHT); } catch {}
};

/**
 * Seed a larger-than-default chat panel size for a user who just signed up.
 *
 * Called once from the signup flows (email + OAuth) before navigating into the
 * new user's space. Only writes when no size is stored yet, so it never
 * clobbers a returning user who previously dragged the panel to their own
 * preferred width. Height is intentionally left unset (null) so the panel keeps
 * its tall CSS default.
 */
export const seedFirstSignupChatPanelSize = (): void => {
  if (typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(KEY_SPATIAL_WIDTH) === null) {
      writeChatPanelSpatialWidth(CHAT_PANEL_SPATIAL_FIRST_SIGNUP_PX);
    }
    if (window.localStorage.getItem(KEY_FOCUSED_WIDTH) === null) {
      writeChatPanelWidth(CHAT_PANEL_FOCUSED_FIRST_SIGNUP_PX);
    }
  } catch {
    // localStorage unavailable (private mode/quota) — the panel just uses its
    // normal default size. No fallback needed.
  }
};

/**
 * Seed a slightly-larger-than-default DOCKED (spatial) width when the in-chat
 * default onboarding flow ends and the panel docks to the bottom-right.
 *
 * Only writes when no spatial width is stored yet — same no-clobber rule as
 * seedFirstSignupChatPanelSize (which may have already run at signup and set
 * an even wider value; this never overrides that or a user's own resize).
 * Height is left untouched so the panel keeps its responsive CSS default.
 */
export const seedPostOnboardingChatPanelSize = (): void => {
  if (typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(KEY_SPATIAL_WIDTH) === null) {
      writeChatPanelSpatialWidth(CHAT_PANEL_SPATIAL_POST_ONBOARDING_PX);
    }
  } catch {
    // localStorage unavailable (private mode/quota) — the panel just uses its
    // normal default size. No fallback needed.
  }
};

/**
 * Clamp a floating/detached chat panel rect so it always stays fully within
 * the current viewport and never renders above `systemBarInset` (the live
 * height of the Desktop system bar — see `utils/systemBarInset.ts`). Used
 * whenever a rect is loaded from storage, centered for intake, or re-validated
 * on window resize, so all three paths agree on the exact same boundary math
 * instead of each re-implementing (and drifting from) it independently.
 *
 * Mirrors `clampPositionToBounds` in `components/Space/utils/windowUtils.ts`:
 * shrinks width/height to fit (never below the panel's own min size) rather
 * than leaving the rect hanging off an edge or under the bar.
 */
export const clampDetachedRect = (
  rect: { x: number; y: number; width: number; height: number },
  systemBarInset: number,
): { x: number; y: number; width: number; height: number } => {
  if (typeof window === 'undefined') return rect;

  // Real available space below the system bar — NOT floored up to the "ideal"
  // min height. Flooring here would let `height` exceed the viewport itself
  // on short/unusual viewports (short window, tall system bar), pushing the
  // panel's bottom edge past window.innerHeight. Below that size we'd rather
  // shrink under the normal min than overflow off-screen.
  const usableHeight = Math.max(0, window.innerHeight - systemBarInset);
  const width = Math.min(Math.max(rect.width, CHAT_PANEL_SPATIAL_MIN_PX), window.innerWidth);
  const height = Math.min(Math.max(rect.height, CHAT_PANEL_SPATIAL_HEIGHT_MIN_PX), usableHeight);

  const maxX = Math.max(0, window.innerWidth - width);
  const maxY = Math.max(systemBarInset, window.innerHeight - height);
  const x = Math.min(Math.max(rect.x, 0), maxX);
  const y = Math.min(Math.max(rect.y, systemBarInset), maxY);

  return { x, y, width, height };
};

export const readChatPanelHeightVh = (): number => {
  const stored = safeReadNumber(KEY_MOBILE_HEIGHT_VH);
  if (stored === null) return CHAT_PANEL_MOBILE_DEFAULT_VH;
  return clamp(stored, CHAT_PANEL_MOBILE_MIN_VH, CHAT_PANEL_MOBILE_MAX_VH);
};

export const writeChatPanelHeightVh = (vh: number): number => {
  const clamped = clamp(vh, CHAT_PANEL_MOBILE_MIN_VH, CHAT_PANEL_MOBILE_MAX_VH);
  safeWriteNumber(KEY_MOBILE_HEIGHT_VH, clamped);
  return clamped;
};

type Reader = () => number;
type Writer = (n: number) => number;

const useChatPanelSize = (storageKey: string, read: Reader, write: Writer) => {
  const [value, setValue] = useState<number>(read);
  const valueRef = useRef(value);
  valueRef.current = value;

  const set = useCallback(
    (next: number) => {
      // Trust the clamped value returned by `write` so the UI never snaps
      // back from under the user's drag, even if localStorage is read-only.
      const clamped = write(next);
      setValue(clamped);
    },
    [write],
  );

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      const next = read();
      if (next !== valueRef.current) {
        setValue(next);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey, read]);

  return [value, set] as const;
};

export const useChatPanelWidth = () =>
  useChatPanelSize(KEY_FOCUSED_WIDTH, readChatPanelWidth, writeChatPanelWidth);

// Returns [height | null, setHeight]. null means "full container height" (the default).
// Changing the returned setter to null clears the stored value and restores full height.
export const useChatPanelFocusedHeight = (): [number | null, (h: number | null) => void] => {
  const [value, setValue] = useState<number | null>(readChatPanelFocusedHeight);

  const set = useCallback((next: number | null) => {
    if (next === null) {
      clearChatPanelFocusedHeight();
      setValue(null);
    } else {
      const clamped = writeChatPanelFocusedHeight(next);
      setValue(clamped);
    }
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY_FOCUSED_HEIGHT) return;
      setValue(readChatPanelFocusedHeight());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return [value, set];
};

export const useChatPanelSpatialWidth = () =>
  useChatPanelSize(
    KEY_SPATIAL_WIDTH,
    readChatPanelSpatialWidth,
    writeChatPanelSpatialWidth,
  );

// Returns [height | null, setHeight]. null = use CSS default (never set by user).
// Pass null to the setter to clear and restore the CSS default.
export const useChatPanelSpatialHeight = (): [number | null, (h: number | null) => void] => {
  const [value, setValue] = useState<number | null>(readChatPanelSpatialHeight);

  const set = useCallback((next: number | null) => {
    if (next === null) {
      clearChatPanelSpatialHeight();
      setValue(null);
    } else {
      const clamped = writeChatPanelSpatialHeight(next);
      setValue(clamped);
    }
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY_SPATIAL_HEIGHT) return;
      setValue(readChatPanelSpatialHeight());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return [value, set];
};

export const useChatPanelHeightVh = () =>
  useChatPanelSize(
    KEY_MOBILE_HEIGHT_VH,
    readChatPanelHeightVh,
    writeChatPanelHeightVh,
  );
